// The design's photos and sample cards, shown while the catalogs are empty.
//
// The images in public/images are the design canvas's own, AI-generated — no
// licence needed; the hero is its scene generated anew (see HERO_IMAGE).

import type { MoodTile, SampleCard } from '@todo/web-ui/lib/site-ui';

/**
 * Hero photo: the Burj Khalifa over the water from a terrace with date palms —
 * not the design canvas's (an upscale, soft on large screens) but the same
 * scene generated anew by Sanity AI (2026-10-31, 1568 × 672, webp q78), sharp
 * at its own size and lighter than the old 2000 px file.
 */
export const HERO_IMAGE = '/images/hero-skyline.webp';

/** The hero's intrinsic size. */
export const HERO_SIZE = { width: 1568, height: 672 } as const;

/**
 * Smaller copies of the hero for phones (sharp, webp: 1100 q74, 750 q72): a
 * 412 px phone at DPR 2.625 — Lighthouse's Moto G Power — takes the 1100 one.
 * Regenerate them when the photo changes.
 */
export const HERO_SRCSET = [
  '/images/hero-skyline-750.webp 750w',
  '/images/hero-skyline-1100.webp 1100w',
  `${HERO_IMAGE} ${HERO_SIZE.width}w`,
].join(', ');

/** Footer background: marble with a palm leaf on the left. */
export const FOOTER_IMAGE = '/images/footer-marble.webp';

/** "Curated for Your Mood": one tile per mood collection (off the home page while they have no theme rules). */
export const MOOD_TILES: readonly MoodTile[] = [
  { key: 'collections.date-night.title', path: 'date-night', image: '/images/mood-date-night.webp' },
  { key: 'collections.beach-days.title', path: 'beach-days', image: '/images/mood-beach-days.webp' },
  { key: 'collections.art-and-culture.title', path: 'art-and-culture', image: '/images/mood-art-culture.webp' },
  { key: 'collections.luxury.title', path: 'luxury', image: '/images/mood-luxury.webp' },
  { key: 'collections.adventure.title', path: 'adventure', image: '/images/mood-adventure.webp' },
];

/** The three "Featured This Week" cards of the design (copy under `sample.<key>`). */
export const SAMPLE_CARDS: readonly SampleCard[] = [
  { key: 'dinner', path: 'date-night', image: '/images/featured-dinner-view.webp' },
  { key: 'safari', path: 'desert-safari', image: '/images/featured-desert-safari.webp' },
  { key: 'yacht', path: 'boats', image: '/images/featured-yacht.webp' },
];
